import React, { useEffect, useState } from 'react'
import {
  Box,
  TextField,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  MenuItem
} from '@mui/material'
import { FormatIndentIncrease, Visibility, VisibilityOff } from '@mui/icons-material'
import { Controller, Control, FieldErrors, useController, useWatch } from 'react-hook-form'
import { validateJSON, formatJSON } from '../../../../utils/validateJSON'
import JsonView from '@uiw/react-json-view'
import { useQuery } from '@tanstack/react-query'
import { useContext } from 'react'
import { ClientContext } from '../../../../utils/clientContext'
import { ReservationService } from '../../api/reservation-service'

export interface VehicleFormData {
  programId: string
  financier: string
  vin: string
  make: string
  model: string
  trim: string
  year: number
  exteriorColor: string
  interiorColor: string
  startingOdometerMiles: number
  additionalDetails: string
}

interface AddVehicleFormProps {
  errors: FieldErrors<VehicleFormData>
  control: Control<VehicleFormData>
}

export const AddVehicleForm: React.FC<AddVehicleFormProps> = ({
  errors,
  control
}) => {
  const [showJsonPreview, setShowJsonPreview] = useState(false)
  const [jsonPreviewData, setJsonPreviewData] = useState(null)
  const clientContext = useContext(ClientContext)
  const clientId = clientContext?.clientId || ''
  const selectedProgramId = useWatch({ control, name: 'programId' })
  const {
    field: makeField,
  } = useController({
    name: 'make',
    control,
    rules: { required: 'Make is required' },
  })
  const {
    field: modelField,
  } = useController({
    name: 'model',
    control,
    rules: { required: 'Model is required' },
  })

  const { data: programsData } = useQuery({
    queryKey: ['/listPrograms', clientId],
    queryFn: () => ReservationService.listPrograms(clientId),
    initialData: { programsList: [] },
    enabled: !!clientId,
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const programs = Array.isArray(programsData?.programsList) ? programsData.programsList : []
  const selectedProgram = programs.find((program) => program.id === selectedProgramId)

  useEffect(() => {
    makeField.onChange(selectedProgram?.make || '')
    modelField.onChange(selectedProgram?.model || '')
  }, [makeField, modelField, selectedProgram])

  const handleJsonPreview = (jsonString: string) => {
    if (!jsonString || jsonString.trim() === '') {
      setJsonPreviewData(null)
      setShowJsonPreview(!showJsonPreview)
      return
    }

    try {
      const parsed = JSON.parse(jsonString)
      setJsonPreviewData(parsed)
    } catch (e) {
      setJsonPreviewData(null)
      console.log(e)
    }
    setShowJsonPreview(!showJsonPreview)
  }

  const handleFormatJson = (field: { value: string; onChange: (value: string) => void }) => {
    const formatted = formatJSON(field.value)
    field.onChange(formatted)
  }
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Vehicle Information
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{xs:12, sm:6}}>
          <Controller
            name="programId"
            control={control}
            rules={{ required: 'Program ID is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                select
                label="Program"
                onChange={(event) => {
                  field.onChange(event)
                }}
                error={!!errors.programId}
                helperText={errors.programId?.message}
              >
                {programs.map((program) => (
                  <MenuItem key={program.id} value={program.id}>
                    {program.description}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid size={{xs:12, sm:6}}>
          <Controller
            name="financier"
            control={control}
            rules={{ required: 'Financier is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                select
                label="Financier"
                error={!!errors.financier}
                helperText={errors.financier?.message}
              >
                <MenuItem value="" disabled>
                  Choose a financier
                </MenuItem>
                <MenuItem value="Spouting Rock">Spouting Rock</MenuItem>
                <MenuItem value="VW Credit">VW Credit</MenuItem>
              </TextField>
            )}
          />
        </Grid>

        <Grid size={{xs:12, sm:6}}>
          <Controller
            name="vin"
            control={control}
            rules={{ required: 'VIN is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="VIN"
                error={!!errors.vin}
                helperText={errors.vin?.message}
              />
            )}
          />
        </Grid>

        <Grid size={{xs:12, sm:6}}>
          <TextField
            {...makeField}
            fullWidth
            label="Make"
            disabled
            error={!!errors.make}
            helperText={errors.make?.message}
          />
        </Grid>

        <Grid size={{xs:12, sm:6}}>
          <TextField
            {...modelField}
            fullWidth
            label="Model"
            disabled
            error={!!errors.model}
            helperText={errors.model?.message}
          />
        </Grid>

        <Grid size={{xs:12, sm:6}}>
          <Controller
            name="trim"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Trim"
                error={!!errors.trim}
                helperText={errors.trim?.message}
              />
            )}
          />
        </Grid>

        <Grid size={{xs:12, sm:6}}>
          <Controller
            name="year"
            control={control}
            rules={{
              required: 'Year is required',
              min: { value: 1900, message: 'Year must be after 1900' },
              max: { value: new Date().getFullYear() + 1, message: 'Year cannot be in the future' }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Year"
                type="number"
                error={!!errors.year}
                helperText={errors.year?.message}
                onChange={(e) => field.onChange(parseInt(e.target.value) || '')}
              />
            )}
          />
        </Grid>

        <Grid size={{xs:12, sm:6}}>
          <Controller
            name="exteriorColor"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Exterior Color"
                error={!!errors.exteriorColor}
                helperText={errors.exteriorColor?.message}
              />
            )}
          />
        </Grid>

        <Grid size={{xs:12, sm:6}}>
          <Controller
            name="interiorColor"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Interior Color"
                error={!!errors.interiorColor}
                helperText={errors.interiorColor?.message}
              />
            )}
          />
        </Grid>

        <Grid size={{xs:12, sm:6}}>
          <Controller
            name="startingOdometerMiles"
            control={control}
            rules={{
              required: 'Starting odometer miles is required',
              min: { value: 0, message: 'Miles cannot be negative' }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Starting Odometer Miles"
                type="number"
                error={!!errors.startingOdometerMiles}
                helperText={errors.startingOdometerMiles?.message}
                onChange={(e) => field.onChange(parseInt(e.target.value) || '')}
              />
            )}
          />
        </Grid>

        <Grid size={{xs:12, sm:6}}>
          <Controller
            name="additionalDetails"
            control={control}
            rules={{
              validate: (value) => {
                if (!value || value.trim() === '') return true
                const validation = validateJSON(value)
                return validation.isValid || validation.error
              }
            }}
            render={({ field }) => (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2">
                    Additional Details (JSON)
                  </Typography>
                  <Tooltip title="Format JSON">
                    <IconButton
                      size="small"
                      onClick={() => handleFormatJson(field)}
                      disabled={!field.value}
                    >
                      <FormatIndentIncrease fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={showJsonPreview ? 'Hide preview' : 'Show preview'}>
                    <IconButton
                      size="small"
                      onClick={() => handleJsonPreview(field.value)}
                      disabled={!field.value}
                    >
                      {showJsonPreview ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  rows={4}
                  placeholder='{"key": "value"}'
                  error={!!errors.additionalDetails}
                  helperText={errors.additionalDetails?.message || 'Enter valid JSON format'}
                />
                {showJsonPreview && jsonPreviewData && (
                  <Box sx={{ mt: 2, maxHeight: 300, overflow: 'auto' }}>
                    <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>
                      JSON Preview:
                    </Typography>
                    <JsonView
                      displayDataTypes={false}
                      value={jsonPreviewData}
                      collapsed={1}
                      style={{
                        width: '100%',
                        overflowX: 'auto',
                      }}
                    />
                  </Box>
                )}
              </Box>
            )}
          />
        </Grid>
      </Grid>
    </Box>
  )
}
